import React, { useCallback, useEffect, useState } from 'react'
import moment from 'moment'
import queryString from 'query-string'
import { Link, useHistory, useLocation } from 'react-router-dom'
import AdminPicker from '../../../components/AdminPicker/AdminPicker'
import { useAuth, useProtectedPage } from '../../../context/AuthContext'
import callAPI from '../../../helpers/callAPI'
import Dollars from '../../../templates/Dollars/Dollars'
import ReportTable from '../../../templates/ReportTable/ReportTable'
import Loading from '../../../templates/UI/Loading/Loading'
import PerformanceTargetModal from './PerformanceTargetModal/PerformanceTargetModal'

const STAFF_GROUPS = {
    affiliate: { label: 'Affiliate', roles: ['AFFILIATE_DIRECTOR', 'AM_SENIOR', 'AM_JR_TEAMLEAD', 'AM_JUNIOR'] },
    advertiser: { label: 'Advertiser', roles: ['ADVERTISER_DIRECTOR', 'MERMGR'] },
    bizdev: { label: 'Bizdev', roles: ['BIZDEV', 'BIZDEV_DIRECTOR'] },
}

const getQuarterMonths = (quarter) => {
    const startMonth = (quarter - 1) * 3 + 1
    return [startMonth, startMonth + 1, startMonth + 2]
}

const getMonthStart = (yr, month) => moment(`${yr}-${month}`, 'YYYY-M').startOf('month').format('YYYY-MM-DD')
const getMonthEnd = (yr, month) => moment(`${yr}-${month}`, 'YYYY-M').endOf('month').format('YYYY-MM-DD')

const buildTableRows = (targets) => {
    const groups = {}

    targets.forEach((target) => {
        const key = `${target.admin_id}-${target.target_yr}-${target.target_quarter}`
        if (!groups[key]) {
            groups[key] = { ...target, months: new Set() }
        }
        groups[key].months.add(target.target_month)
    })

    const rows = [...targets]

    Object.values(groups).forEach((group) => {
        getQuarterMonths(group.target_quarter).forEach((month) => {
            if (!group.months.has(month)) {
                rows.push({
                    isMissing: true,
                    admin_id: group.admin_id,
                    admin_name: group.admin_name,
                    team: group.team,
                    target_yr: group.target_yr,
                    target_quarter: group.target_quarter,
                    target_month: month,
                    role_id: group.role_id,
                    role: group.role,
                    manager_id: group.manager_id,
                    manager_name: group.manager_name,
                })
            }
        })
    })

    return rows.sort((a, b) => {
        const nameCompare = (a.admin_name || '').localeCompare(b.admin_name || '')
        if (nameCompare !== 0) return nameCompare
        if (a.target_yr !== b.target_yr) return b.target_yr - a.target_yr
        if (a.target_quarter !== b.target_quarter) return b.target_quarter - a.target_quarter
        return a.target_month - b.target_month
    })
}

const PerformanceManagement = ({ showSystemMessage }) => {
    document.title = 'Performance Management'

    const auth = useAuth()
    const history = useHistory()
    const location = useLocation()
    const urlParams = queryString.parse(location.search)

    useProtectedPage(auth.permissions, 'performance_management')

    const [staffGroup, setStaffGroup] = useState(urlParams.staffGroup && STAFF_GROUPS[urlParams.staffGroup] ? urlParams.staffGroup : '')
    const [adminId, setAdminId] = useState(urlParams.adminId ? parseInt(urlParams.adminId, 10) : 0)
    const [roleId, setRoleId] = useState(null)
    const [roleName, setRoleName] = useState('')
    const [adminName, setAdminName] = useState('')
    const [managerId, setManagerId] = useState(null)
    const [managerName, setManagerName] = useState('')
    const [selectedTarget, setSelectedTarget] = useState(null)
    const [modalOpen, setModalOpen] = useState(false)
    const [modalMode, setModalMode] = useState('create')
    const [targets, setTargets] = useState([])
    const [tableRows, setTableRows] = useState([])
    const [dataLoaded, setDataLoaded] = useState(true)
    const [showAll, setShowAll] = useState(false)

    const loadTargets = useCallback((silent = false) => {
        if (showAll) {
            if (!silent) setDataLoaded(false)
            return callAPI('admin/performanceManagement?showAll=true', 'GET').then((result) => {
                if (result.success) {
                    const loadedTargets = result.targets || []
                    setTargets(loadedTargets)
                    setTableRows(buildTableRows(loadedTargets))
                } else {
                    showSystemMessage('error', result.errors.message)
                    setTargets([])
                    setTableRows([])
                }
                setDataLoaded(true)
            })
        }

        if (!silent) setDataLoaded(false)
        return callAPI(`admin/performanceManagement?adminId=${adminId}`, 'GET').then((result) => {
            if (result.success) {
                const loadedTargets = result.targets || []
                setTargets(loadedTargets)
                setTableRows(buildTableRows(loadedTargets))
            } else {
                showSystemMessage('error', result.errors.message)
                setTargets([])
                setTableRows([])
            }
            setDataLoaded(true)
        })
    }, [adminId, showAll])

    useEffect(() => {
        if (showAll) {
            loadTargets()
            return
        }

        if (adminId > 0) {
            callAPI(`admin/user/${adminId}`, 'GET').then((result) => {
                if (result.success) {
                    setRoleId(result.admin.role_id)
                    setRoleName(result.admin.role || '')
                    setAdminName(result.admin.name)
                    setManagerId(result.admin.manager.id || null)
                    setManagerName(result.admin.manager.name || '')
                }
            })

            loadTargets()
        }
    }, [adminId, showAll, loadTargets])

    useEffect(() => {
        const params = new URLSearchParams()
        if (staffGroup) params.set('staffGroup', staffGroup)
        if (adminId > 0) params.set('adminId', adminId)
        history.push(`/admin/performanceManagement?${params.toString()}`)
    }, [staffGroup, adminId, history])

    const canCreate = staffGroup && adminId > 0 && !showAll
    const missingCount = tableRows.filter((row) => row.isMissing).length

    const columns = [
        ...(showAll
            ? [
                  {
                      Header: 'Name',
                      accessor: 'admin_name',
                      Cell: ({ row }) => {
                          const { admin_name, admin_id } = row.original
                          if (!admin_id) return <>{admin_name}</>
                          return <Link to={`/dashboard/seniorAM?adminId=${admin_id}`}>{admin_name}</Link>
                      },
                  },
                  {
                      Header: 'Team',
                      accessor: 'team',
                      Cell: ({ cell: { value } }) => <>{value}</>,
                  },
              ]
            : []),
        {
            Header: 'Year',
            accessor: 'target_yr',
            Cell: ({ cell: { value } }) => <>{value}</>,
        },
        { Header: 'Quarter', accessor: 'target_quarter', Cell: ({ cell: { value } }) => <>Q{value}</> },
        {
            Header: 'Month',
            accessor: 'target_month',
            Cell: ({ row, cell: { value } }) => {
                const monthName = moment().month(value - 1).format('MMMM')
                if (row.original.isMissing) {
                    return <span className="text-danger font-weight-semibold">{monthName} Missing</span>
                }
                return <>{monthName}</>
            },
        },
        {
            Header: 'Amount',
            accessor: 'target_month_amount',
            Cell: ({ row, cell: { value } }) => (row.original.isMissing ? <>—</> : <Dollars value={value} />),
        },
        {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ row, cell: { value } }) => {
                if (row.original.isMissing) return <>Missing</>
                if (value === 'P') return <span className="text-info">Pending</span>
                if (value === 'A') return <>Active</>
                return <>{value}</>
            },
        },
        {
            Header: 'Start',
            accessor: 'start_date',
            Cell: ({ row }) => (
                <>{row.original.isMissing ? '' : getMonthStart(row.original.target_yr, row.original.target_month)}</>
            ),
        },
        {
            Header: 'End',
            accessor: 'end_date',
            Cell: ({ row }) => (
                <>{row.original.isMissing ? '' : getMonthEnd(row.original.target_yr, row.original.target_month)}</>
            ),
        },
        {
            Header: '',
            accessor: 'actions',
            disableSortBy: true,
            Cell: ({ row }) => {
                if (row.original.isMissing) {
                    // if (!isFutureMonth(row.original.target_yr, row.original.target_month)) {
                    //     return null
                    // }
                    return (
                        <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            onClick={(e) => {
                                e.stopPropagation()
                                setSelectedTarget(row.original)
                                setModalMode('restore')
                                setModalOpen(true)
                            }}
                        >
                            Add
                        </button>
                    )
                }

                return (
                    <button
                        type="button"
                        className="btn btn-sm btn-warning"
                        onClick={(e) => {
                            e.stopPropagation()
                            setSelectedTarget(row.original)
                            setModalMode('edit')
                            setModalOpen(true)
                        }}
                    >
                        Edit
                    </button>
                )
            },
        },
    ]

    const useTargetData = selectedTarget && (modalMode === 'edit' || modalMode === 'restore')
    const editAdminId = useTargetData ? selectedTarget.admin_id : adminId
    const editAdminName = useTargetData ? selectedTarget.admin_name : adminName
    const editRoleId = useTargetData ? selectedTarget.role_id : roleId
    const editRoleName = useTargetData ? selectedTarget.role : roleName
    const editManagerId = useTargetData ? selectedTarget.manager_id : managerId
    const editManagerName = useTargetData ? selectedTarget.manager_name : managerName

    const showTable = showAll || adminId > 0

    return (
        <div>
            {modalOpen && (
                <PerformanceTargetModal
                    showSystemMessage={showSystemMessage}
                    isOpen={modalOpen}
                    handleClose={(saved) => {
                        setModalOpen(false)
                        setSelectedTarget(null)
                        if (saved) loadTargets(true)
                    }}
                    mode={modalMode}
                    adminId={editAdminId}
                    adminName={editAdminName}
                    roleId={editRoleId}
                    roleName={editRoleName}
                    managerId={editManagerId}
                    managerName={editManagerName}
                    createdByName={auth.user.name}
                    target={selectedTarget}
                    existingTargets={targets}
                />
            )}
            <div className="page-header d-flex align-items-center">
                <h1 className="mb-h1">Performance Management</h1>
            </div>
            <div className="row">
                <div className="col-lg-10">
                    <div className="card">
                        <div className="d-flex justify-content-between p-3 align-items-center flex-wrap">
                            <div className="d-flex align-items-center">
                                <h1 className="mb-h1-dark mr-3 mb-0">Performance Targets</h1>
                                <div className="d-flex align-items-center flex-nowrap">
                                    <select
                                        className="form-control form-control-sm mr-2"
                                        style={{ minWidth: '130px', width: 'auto' }}
                                        value={staffGroup}
                                        onChange={(e) => {
                                            setShowAll(false)
                                            setStaffGroup(e.target.value)
                                            setAdminId(0)
                                            setSelectedTarget(null)
                                        }}
                                    >
                                        <option value="">Select Team</option>
                                        {Object.entries(STAFF_GROUPS).map(([key, group]) => (
                                            <option key={key} value={key}>
                                                {group.label} Team
                                            </option>
                                        ))}
                                    </select>
                                    {staffGroup ? (
                                        <AdminPicker
                                            key={staffGroup}
                                            adminId={adminId}
                                            setAdminId={(id) => {
                                                setShowAll(false)
                                                setAdminId(parseInt(id, 10) || 0)
                                                setSelectedTarget(null)
                                            }}
                                            role={STAFF_GROUPS[staffGroup].roles}
                                            placeholder="Select name"
                                        />
                                    ) : (
                                        <select className="form-control form-control-sm" style={{ minWidth: '150px' }} value="" disabled>
                                            <option value="">Select name</option>
                                        </select>
                                    )}
                                    <button
                                        type="button"
                                        className="btn btn-primary btn-sm text-nowrap ml-2 flex-shrink-0"
                                        onClick={() => {
                                            setShowAll(true)
                                            setStaffGroup('')
                                            setAdminId(0)
                                            setSelectedTarget(null)
                                        }}
                                    >
                                        Show All
                                    </button>
                                </div>
                            </div>
                            {canCreate && (
                                <div className="d-flex mt-2 mt-md-0">
                                    <button
                                        type="button"
                                        className="btn btn-warning btn-sm"
                                        onClick={() => {
                                            setSelectedTarget(null)
                                            setModalMode('create')
                                            setModalOpen(true)
                                        }}
                                    >
                                        Create New Performance Target
                                    </button>
                                </div>
                            )}
                        </div>
                        {!canCreate && (
                            <div className="px-3 pb-2 text-muted small">
                                Select team and name to create new Performance Target
                            </div>
                        )}
                        {!showTable ? (
                            <div className="p-3">Select a staff member to view performance targets, or click Show All.</div>
                        ) : dataLoaded ? (
                            targets.length === 0 ? (
                                <div className="p-3">No performance targets found.</div>
                            ) : (
                                <>
                                    {missingCount > 0 && (
                                        <div className="alert alert-warning mx-3 mb-0">
                                            {missingCount} month(s) missing from quarter targets. Please Add data.
                                        </div>
                                    )}
                                    <ReportTable
                                        data={tableRows}
                                        columns={columns}
                                        getRowProps={(row) => (row.original.isMissing ? { className: 'bgWarning25' } : {})}
                                    />
                                </>
                            )
                        ) : (
                            <Loading />
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PerformanceManagement
